// Finance Module - Redesigned with charts and AI agent
import React, { useState, useEffect, useRef } from 'react';
import OkTradeModule from './OkTradeModule';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, TrendingUp, TrendingDown, DollarSign, CreditCard,
  Plus, Mic, MicOff, Calculator, RefreshCw, Search,
  ArrowUpRight, ArrowDownRight, Bitcoin, Target, ChevronRight, X
} from 'lucide-react';

const CRYPTO_META = {
  'BTC':  { color: '#F7931A', bg: '#F7931A22', emoji: '₿',  logo: 'https://assets.coingecko.com/coins/images/1/thumb/bitcoin.png' },
  'ETH':  { color: '#627EEA', bg: '#627EEA22', emoji: 'Ξ',  logo: 'https://assets.coingecko.com/coins/images/279/thumb/ethereum.png' },
  'BNB':  { color: '#F3BA2F', bg: '#F3BA2F22', emoji: '⬡',  logo: 'https://assets.coingecko.com/coins/images/825/thumb/bnb-icon2_2x.png' },
  'SOL':  { color: '#9945FF', bg: '#9945FF22', emoji: '◎',  logo: 'https://assets.coingecko.com/coins/images/4128/thumb/solana.png' },
  'XRP':  { color: '#0085C0', bg: '#0085C022', emoji: '✕',  logo: 'https://assets.coingecko.com/coins/images/44/thumb/xrp-symbol-white-128.png' },
  'ADA':  { color: '#0033AD', bg: '#0033AD22', emoji: '₳',  logo: 'https://assets.coingecko.com/coins/images/975/thumb/cardano.png' },
  'DOGE': { color: '#C2A633', bg: '#C2A63322', emoji: 'Ð',  logo: 'https://assets.coingecko.com/coins/images/5/thumb/dogecoin.png' },
  'MATIC':{ color: '#8247E5', bg: '#8247E522', emoji: '⬡',  logo: 'https://assets.coingecko.com/coins/images/4713/thumb/matic-token-icon.png' },
  'DOT':  { color: '#E6007A', bg: '#E6007A22', emoji: '●',  logo: 'https://assets.coingecko.com/coins/images/12171/thumb/polkadot.png' },
  'AVAX': { color: '#E84142', bg: '#E8414222', emoji: '▲',  logo: 'https://assets.coingecko.com/coins/images/12559/thumb/Avalanche_Circle_RedWhite_Trans.png' },
  'LINK': { color: '#2A5ADA', bg: '#2A5ADA22', emoji: '⬡',  logo: 'https://assets.coingecko.com/coins/images/877/thumb/chainlink-new-logo.png' },
  'UNI':  { color: '#FF007A', bg: '#FF007A22', emoji: '🦄',  logo: 'https://assets.coingecko.com/coins/images/12504/thumb/uni.jpg' },
  'LTC':  { color: '#BFBBBB', bg: '#BFBBBB22', emoji: 'Ł',  logo: 'https://assets.coingecko.com/coins/images/2/thumb/litecoin.png' },
};

const INVESTMENT_TIPS = [
  { emoji: '📊', title: 'Diversifica tu portafolio', tip: 'No pongas todo en una sola cripto. Combina Bitcoin (reserva de valor), Ethereum (smart contracts) y altcoins de menor cap para equilibrar riesgo.' },
  { emoji: '⏰', title: 'Dollar Cost Averaging (DCA)', tip: 'Invierte una cantidad fija cada semana o mes. Esto reduce el impacto de la volatilidad y promedia tu precio de compra a largo plazo.' },
  { emoji: '🔒', title: 'Custodia propia', tip: 'Mueve tus criptos a una billetera hardware si tienes más de $500 USD. "Not your keys, not your coins." Usa Ledger o Trezor.' },
  { emoji: '📉', title: 'Gestión de riesgo', tip: 'Invierte solo lo que estás dispuesto a perder. Las criptos pueden caer 80% en meses. Mantén siempre liquidez en moneda local.' },
  { emoji: '🧠', title: 'Investiga antes de comprar (DYOR)', tip: 'Lee el whitepaper, revisa el equipo fundador, la tokenomics y el volumen de transacciones reales antes de invertir.' },
  { emoji: '🚫', title: 'Evita el FOMO y el FUD', tip: 'El miedo a perderse algo (FOMO) y el pánico (FUD) son los errores más costosos. Sigue tu estrategia sin dejarte llevar por el mercado.' },
];

const CryptoSparkline = ({ symbol, change24h = 0, width = 80, height = 32 }) => {
  const isUp = change24h >= 0;
  const color = isUp ? '#22c55e' : '#ef4444';
  const seed = (symbol || 'X').charCodeAt(0);
  const points = [];
  let val = 50;
  for (let i = 0; i < 20; i++) {
    val += Math.sin((seed * i + i * 2.1) * 0.5) * 3 + (change24h / 10);
    val = Math.max(5, Math.min(95, val));
    points.push(val);
  }
  const min = Math.min(...points), max = Math.max(...points);
  const range = max - min || 1;
  const norm = points.map(p => ((p - min) / range) * (height - 6) + 3);
  const pathD = norm.map((y, i) => `${i === 0 ? 'M' : 'L'} ${((i / (points.length - 1)) * width).toFixed(1)} ${(height - y).toFixed(1)}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id={`sg-${symbol}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${pathD} L ${width} ${height} L 0 ${height} Z`} fill={`url(#sg-${symbol})`} />
      <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EXPENSE_CATEGORIES = ['Alimentación', 'Transporte', 'Vivienda', 'Servicios', 'Entretenimiento', 'Salud', 'Educación', 'Ropa', 'Tecnología', 'Otros'];
const CAT_COLORS = ['#ef4444','#f97316','#f59e0b','#22c55e','#3b82f6','#ec4899','#8b5cf6','#14b8a6','#06b6d4','#6b7280'];

const TABS = [
  { id: 'oktrade', label: 'OkTrade', emoji: '⚡' },
  { id: 'overview', label: 'Resumen', emoji: '📊' },
  { id: 'expenses', label: 'Gastos', emoji: '💸' },
  { id: 'budget', label: 'Presupuesto', emoji: '🎯' },
  { id: 'investments', label: 'Inversiones', emoji: '📈' },
  { id: 'debts', label: 'Deudas', emoji: '💳' },
  { id: 'crypto', label: 'Cripto', emoji: '₿' },
  { id: 'converter', label: 'Divisas', emoji: '💱' },
];

const FinanceModule = ({ user }) => {
  const [activeTab, setActiveTab] = useState('oktrade');
  const [budgets, setBudgets] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [debts, setDebts] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [listening, setListening] = useState(false);

  const [newExpense, setNewExpense] = useState({ amount: '', category: 'Alimentación', description: '', date: new Date().toISOString().split('T')[0] });
  const [newBudget, setNewBudget] = useState({ name: '', amount: '', category: 'Alimentación', period: 'monthly' });
  const [newIncome, setNewIncome] = useState({ amount: '', source: '', date: new Date().toISOString().split('T')[0] });

  const [calcPrincipal, setCalcPrincipal] = useState(10000);
  const [calcRate, setCalcRate] = useState(10);
  const [calcYears, setCalcYears] = useState(5);
  const [calcResult, setCalcResult] = useState(null);

  const [convertAmount, setConvertAmount] = useState(100);
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('MXN');
  const [convertResult, setConvertResult] = useState(null);
  const [cryptoSearch, setCryptoSearch] = useState('');

  const recognitionRef = useRef(null);

  useEffect(() => {
    loadData();
    setupRecognition();
  }, [user.id]);

  const setupRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'es-MX';
    r.onresult = (e) => {
      const text = e.results[0][0].transcript;
      const match = text.match(/(\d+)\s*(pesos|en)?\s*(.*)/i);
      if (match) setNewExpense(p => ({ ...p, amount: match[1], description: match[3] || '' }));
      setListening(false);
    };
    r.onerror = r.onend = () => setListening(false);
    recognitionRef.current = r;
  };

  const handleVoice = () => {
    if (!recognitionRef.current) { toast.error('Voz no disponible'); return; }
    if (listening) { recognitionRef.current.stop(); return; }
    recognitionRef.current.start();
    setListening(true);
  };

  const loadData = async () => {
    try {
      const [budgetRes, expenseRes, incomeRes, investRes, debtRes, summaryRes, cryptoRes] = await Promise.all([
        axios.get(`${API}/finance/budgets/${user.id}`),
        axios.get(`${API}/finance/expenses/${user.id}`),
        axios.get(`${API}/finance/income/${user.id}`),
        axios.get(`${API}/finance/investments/${user.id}`),
        axios.get(`${API}/finance/debts/${user.id}`),
        axios.get(`${API}/finance/expenses/${user.id}/summary`),
        axios.get(`${API}/finance/crypto/prices`),
      ]);
      setBudgets(budgetRes.data);
      setExpenses(expenseRes.data);
      setIncome(incomeRes.data);
      setInvestments(investRes.data);
      setDebts(debtRes.data);
      setExpenseSummary(summaryRes.data);
      setCryptoPrices(cryptoRes.data.prices || []);
    } catch (e) {}
  };

  const addExpense = async () => {
    if (!newExpense.amount) return;
    try {
      await axios.post(`${API}/finance/expenses?user_id=${user.id}`, { ...newExpense, amount: parseFloat(newExpense.amount) });
      toast.success('Gasto registrado');
      setNewExpense({ amount: '', category: 'Alimentación', description: '', date: new Date().toISOString().split('T')[0] });
      loadData();
    } catch { toast.error('Error al registrar'); }
  };

  const addBudget = async () => {
    if (!newBudget.name || !newBudget.amount) return;
    try {
      await axios.post(`${API}/finance/budgets?user_id=${user.id}`, { ...newBudget, amount: parseFloat(newBudget.amount), start_date: new Date().toISOString() });
      toast.success('Presupuesto creado');
      setNewBudget({ name: '', amount: '', category: 'Alimentación', period: 'monthly' });
      loadData();
    } catch { toast.error('Error'); }
  };

  const calculateInvestment = async () => {
    try {
      const res = await axios.get(`${API}/finance/investments/${user.id}/calculator?principal=${calcPrincipal}&rate=${calcRate}&years=${calcYears}`);
      setCalcResult(res.data);
    } catch (e) {}
  };

  const convertCurrency = async () => {
    try {
      const res = await axios.get(`${API}/finance/currency/convert?amount=${convertAmount}&from_currency=${fromCurrency}&to_currency=${toCurrency}`);
      setConvertResult(res.data);
    } catch (e) {}
  };

  const totalIncome = income.reduce((s, i) => s + (i.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const balance = totalIncome - totalExpenses;

  // Bar chart from expense summary
  const renderExpenseChart = () => {
    if (!expenseSummary?.by_category) return null;
    const cats = Object.entries(expenseSummary.by_category);
    if (!cats.length) return null;
    const max = Math.max(...cats.map(([,v]) => v));
    return (
      <div className="space-y-2">
        {cats.map(([cat, amount], idx) => (
          <div key={cat} className="flex items-center gap-3">
            <span className="text-gray-400 text-xs w-24 truncate">{cat}</span>
            <div className="flex-1 h-6 bg-white/5 rounded-lg overflow-hidden">
              <div className="h-full rounded-lg transition-all flex items-center pl-2"
                style={{ width: `${(amount / max) * 100}%`, backgroundColor: CAT_COLORS[idx % CAT_COLORS.length] + '40', borderLeft: `3px solid ${CAT_COLORS[idx % CAT_COLORS.length]}` }}>
              </div>
            </div>
            <span className="text-white text-xs font-medium w-20 text-right">${amount.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 min-h-screen">
      <SectionAgent section="finance" />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wallet className="text-emerald-400" size={24} />Finanzas
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Control total de tus finanzas personales</p>
        </div>
        <button onClick={handleVoice}
          className={`p-2.5 rounded-xl transition-colors ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
          title="Registrar gasto por voz">
          {listening ? <MicOff size={18} /> : <Mic size={18} />}
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="glass-panel p-4 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
              <ArrowUpRight className="text-green-400" size={16} />
            </div>
            <span className="text-gray-400 text-sm">Ingresos</span>
          </div>
          <p className="text-2xl font-bold text-green-400">${totalIncome.toLocaleString()}</p>
          <p className="text-gray-600 text-xs mt-1">{income.length} movimiento{income.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="glass-panel p-4 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
              <ArrowDownRight className="text-red-400" size={16} />
            </div>
            <span className="text-gray-400 text-sm">Gastos</span>
          </div>
          <p className="text-2xl font-bold text-red-400">${totalExpenses.toLocaleString()}</p>
          <p className="text-gray-600 text-xs mt-1">{expenses.length} gasto{expenses.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="glass-panel p-4 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${balance >= 0 ? 'bg-blue-500/20' : 'bg-red-500/20'}`}>
              <Wallet className={balance >= 0 ? 'text-blue-400' : 'text-red-400'} size={16} />
            </div>
            <span className="text-gray-400 text-sm">Balance</span>
          </div>
          <p className={`text-2xl font-bold ${balance >= 0 ? 'text-blue-400' : 'text-red-400'}`}>${balance.toLocaleString()}</p>
          <p className="text-gray-600 text-xs mt-1">{balance >= 0 ? 'Positivo ✅' : 'Negativo ⚠️'}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar pb-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm whitespace-nowrap transition-all ${activeTab === t.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
            <span>{t.emoji}</span>{t.label}
          </button>
        ))}
      </div>

      {/* Tab: OkTrade */}
      {activeTab === 'oktrade' && (
        <div className="-mx-4 -mb-4" style={{ height: 'calc(100vh - 140px)' }}>
          <OkTradeModule user={user} />
        </div>
      )}

      {/* Tab: Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Quick Add Expense */}
          <div className="glass-panel p-4 rounded-2xl">
            <h3 className="text-white font-semibold mb-3 text-sm flex items-center gap-2">
              <Plus size={16} className="text-red-400" />Registrar Gasto Rápido
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <input type="number" value={newExpense.amount} onChange={e => setNewExpense(p => ({ ...p, amount: e.target.value }))}
                placeholder="Cantidad ($)" className="input-glass text-sm" />
              <select value={newExpense.category} onChange={e => setNewExpense(p => ({ ...p, category: e.target.value }))} className="input-glass text-sm">
                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="text" value={newExpense.description} onChange={e => setNewExpense(p => ({ ...p, description: e.target.value }))}
                placeholder="Descripción" className="input-glass text-sm" />
              <button onClick={addExpense} className="btn-primary flex items-center justify-center gap-1.5 text-sm">
                <Plus size={16} />Agregar
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Expense chart */}
            <div className="glass-panel p-5 rounded-2xl">
              <h3 className="text-white font-semibold mb-4 text-sm">Gastos por Categoría</h3>
              {expenseSummary ? renderExpenseChart() : <p className="text-gray-600 text-sm text-center py-6">Sin datos aún</p>}
            </div>

            {/* Recent expenses */}
            <div className="glass-panel p-5 rounded-2xl">
              <h3 className="text-white font-semibold mb-4 text-sm">Últimos Movimientos</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                {expenses.slice(0, 8).map(exp => (
                  <div key={exp.id} className="flex items-center justify-between p-2.5 rounded-xl hover:bg-white/5 transition-colors">
                    <div>
                      <p className="text-white text-sm">{exp.description || exp.category}</p>
                      <p className="text-gray-500 text-xs">{exp.category} • {exp.date}</p>
                    </div>
                    <span className="text-red-400 font-semibold text-sm">-${exp.amount}</span>
                  </div>
                ))}
                {expenses.length === 0 && <p className="text-gray-600 text-sm text-center py-6">Sin gastos registrados</p>}
              </div>
            </div>
          </div>

          {/* Budget overview */}
          {budgets.length > 0 && (
            <div className="glass-panel p-5 rounded-2xl">
              <h3 className="text-white font-semibold mb-4 text-sm flex items-center gap-2">
                <Target size={16} className="text-yellow-400" />Presupuestos Activos
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {budgets.slice(0, 4).map(b => {
                  const pct = Math.min(((b.spent || 0) / b.amount) * 100, 100);
                  return (
                    <div key={b.id} className="p-3 bg-white/5 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white text-sm font-medium">{b.name}</span>
                        <span className={`text-xs ${pct > 80 ? 'text-red-400' : 'text-gray-400'}`}>${b.spent || 0}/${b.amount}</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Expenses */}
      {activeTab === 'expenses' && (
        <div className="space-y-5">
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-4">Registrar Gasto</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div className="flex gap-2 col-span-2 sm:col-span-1">
                <input type="number" value={newExpense.amount} onChange={e => setNewExpense(p => ({ ...p, amount: e.target.value }))}
                  placeholder="Cantidad ($)" className="input-glass flex-1 text-sm" />
                <button onClick={handleVoice} className={`px-3 rounded-xl ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                  {listening ? <MicOff size={16} /> : <Mic size={16} />}
                </button>
              </div>
              <select value={newExpense.category} onChange={e => setNewExpense(p => ({ ...p, category: e.target.value }))} className="input-glass text-sm">
                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="text" value={newExpense.description} onChange={e => setNewExpense(p => ({ ...p, description: e.target.value }))}
                placeholder="Descripción" className="input-glass text-sm" />
              <button onClick={addExpense} className="btn-primary text-sm flex items-center justify-center gap-1.5">
                <Plus size={16} />Agregar
              </button>
            </div>
          </div>
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-4">Historial de Gastos ({expenses.length})</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
              {expenses.map(exp => (
                <div key={exp.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-red-500/15 flex items-center justify-center">
                      <TrendingDown className="text-red-400" size={16} />
                    </div>
                    <div>
                      <p className="text-white text-sm">{exp.description || exp.category}</p>
                      <p className="text-gray-500 text-xs">{exp.category} • {exp.date}</p>
                    </div>
                  </div>
                  <span className="text-red-400 font-bold">-${exp.amount}</span>
                </div>
              ))}
              {expenses.length === 0 && <p className="text-gray-600 text-center py-8">Sin gastos registrados</p>}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Budget */}
      {activeTab === 'budget' && (
        <div className="space-y-5">
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-4">Crear Presupuesto</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <input type="text" value={newBudget.name} onChange={e => setNewBudget(p => ({ ...p, name: e.target.value }))}
                placeholder="Nombre" className="input-glass text-sm" />
              <input type="number" value={newBudget.amount} onChange={e => setNewBudget(p => ({ ...p, amount: e.target.value }))}
                placeholder="Monto ($)" className="input-glass text-sm" />
              <select value={newBudget.category} onChange={e => setNewBudget(p => ({ ...p, category: e.target.value }))} className="input-glass text-sm">
                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={addBudget} className="btn-primary text-sm flex items-center justify-center gap-1.5">
                <Plus size={16} />Crear
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {budgets.map(b => {
              const pct = Math.min(((b.spent || 0) / b.amount) * 100, 100);
              return (
                <div key={b.id} className="glass-panel p-5 rounded-2xl">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium">{b.name}</h4>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-400">{b.category}</span>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                      <span>Gastado: ${(b.spent || 0).toLocaleString()}</span>
                      <span>de ${b.amount.toLocaleString()}</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <p className={`text-xs ${pct > 80 ? 'text-red-400' : 'text-gray-500'}`}>
                    {pct > 80 ? '⚠️ Cerca del límite' : `${(100 - pct).toFixed(0)}% disponible`}
                  </p>
                </div>
              );
            })}
            {budgets.length === 0 && <div className="glass-panel p-8 text-center col-span-2"><p className="text-gray-600">Sin presupuestos creados</p></div>}
          </div>
        </div>
      )}

      {/* Tab: Investments */}
      {activeTab === 'investments' && (
        <div className="glass-panel p-5 rounded-2xl">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Calculator className="text-green-400" size={18} />Calculadora de Inversiones
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
            {[
              { label: 'Capital inicial ($)', val: calcPrincipal, set: setCalcPrincipal },
              { label: 'Tasa anual (%)', val: calcRate, set: setCalcRate },
              { label: 'Años', val: calcYears, set: setCalcYears },
            ].map(({ label, val, set }) => (
              <div key={label}>
                <label className="text-gray-400 text-xs mb-1 block">{label}</label>
                <input type="number" value={val} onChange={e => set(parseFloat(e.target.value))} className="input-glass w-full text-sm" />
              </div>
            ))}
            <div className="flex items-end">
              <button onClick={calculateInvestment} className="btn-primary w-full text-sm">Calcular</button>
            </div>
          </div>
          {calcResult && (
            <div className="p-5 bg-green-500/10 rounded-2xl border border-green-500/20">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Capital final</p>
                  <p className="text-2xl font-bold text-green-400">${calcResult.final_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Intereses ganados</p>
                  <p className="text-2xl font-bold text-green-400">${calcResult.total_interest?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Rendimiento</p>
                  <p className="text-2xl font-bold text-green-400">{((calcResult.total_interest / calcPrincipal) * 100).toFixed(1)}%</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Debts */}
      {activeTab === 'debts' && (
        <div className="glass-panel p-5 rounded-2xl">
          <h3 className="text-white font-semibold mb-4">Plan de Pago de Deudas</h3>
          {debts.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No tienes deudas registradas</p>
          ) : (
            <div className="space-y-3">
              {debts.map((d, i) => (
                <div key={d.id} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                      <span className="text-red-400 font-bold text-sm">{i + 1}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{d.name}</p>
                      <p className="text-gray-500 text-xs">{d.interest_rate}% interés anual</p>
                    </div>
                  </div>
                  <span className="text-red-400 font-bold">${d.remaining_amount?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Crypto */}
      {activeTab === 'crypto' && (() => {
        const sym = (s) => (s || '').replace('USDT','').replace('USD','').toUpperCase();
        const filtered = cryptoPrices.filter(c =>
          !cryptoSearch ||
          c.name?.toLowerCase().includes(cryptoSearch.toLowerCase()) ||
          sym(c.symbol).toLowerCase().includes(cryptoSearch.toLowerCase())
        );
        return (
          <div className="space-y-5">
            {/* Header + Search */}
            <div className="glass-panel p-5 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Bitcoin className="text-yellow-500" size={18} />Criptomonedas en Tiempo Real
                </h3>
                <span className="text-xs text-gray-500 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-400 inline-block animate-pulse" />Live
                </span>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  value={cryptoSearch}
                  onChange={e => setCryptoSearch(e.target.value)}
                  placeholder="Buscar criptomoneda..."
                  className="input-glass w-full text-sm pl-9"
                />
              </div>

              {/* Crypto Cards */}
              <div className="space-y-2">
                {filtered.map((crypto) => {
                  const s = sym(crypto.symbol);
                  const meta = CRYPTO_META[s] || { color: '#888888', bg: '#88888822', emoji: '●', logo: '' };
                  const change = crypto.change_24h || 0;
                  const price = crypto.price_usd || 0;
                  return (
                    <div key={crypto.symbol} className="flex items-center gap-3 p-3 rounded-2xl border border-white/5 hover:bg-white/5 transition-colors">
                      {/* Logo */}
                      <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden relative"
                        style={{ background: meta.bg, border: `2px solid ${meta.color}55` }}>
                        {meta.logo ? (
                          <img src={meta.logo} alt={s} className="w-7 h-7 object-contain"
                            onError={e => { e.target.style.display='none'; e.target.parentNode.querySelector('.fb-emoji').style.display='flex'; }} />
                        ) : null}
                        <span className="fb-emoji hidden absolute inset-0 items-center justify-center font-bold text-sm" style={{ color: meta.color }}>{meta.emoji}</span>
                      </div>

                      {/* Name + Symbol */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-white font-semibold text-sm truncate">{crypto.name}</p>
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold flex-shrink-0"
                            style={{ background: meta.bg, color: meta.color }}>{s}</span>
                        </div>
                        <p className="text-gray-600 text-xs">Cripto digital</p>
                      </div>

                      {/* Sparkline */}
                      <div className="hidden sm:block flex-shrink-0">
                        <CryptoSparkline symbol={s} change24h={change} />
                      </div>

                      {/* Price + Change */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-white font-bold text-sm">
                          ${price > 1 ? price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : price.toFixed(6)}
                        </p>
                        <div className={`flex items-center justify-end gap-0.5 text-xs font-semibold ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {change >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                          {Math.abs(change).toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  );
                })}
                {filtered.length === 0 && cryptoPrices.length === 0 && (
                  <div className="text-center py-10">
                    <Bitcoin size={36} className="text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">Cargando precios en tiempo real...</p>
                  </div>
                )}
                {filtered.length === 0 && cryptoPrices.length > 0 && (
                  <p className="text-gray-500 text-sm text-center py-6">No se encontró "{cryptoSearch}"</p>
                )}
              </div>
            </div>

            {/* Investment Tips */}
            <div className="glass-panel p-5 rounded-2xl">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="text-green-400" size={18} />Tips de Inversión en Criptomonedas
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {INVESTMENT_TIPS.map((tip, i) => (
                  <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{tip.emoji}</span>
                      <p className="text-white font-semibold text-sm">{tip.title}</p>
                    </div>
                    <p className="text-gray-400 text-xs leading-relaxed">{tip.tip}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tab: Converter */}
      {activeTab === 'converter' && (
        <div className="glass-panel p-5 rounded-2xl">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <RefreshCw className="text-blue-400" size={18} />Conversor de Divisas
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-gray-400 text-xs mb-1 block">Cantidad</label>
              <input type="number" value={convertAmount} onChange={e => setConvertAmount(parseFloat(e.target.value))} className="input-glass w-full text-sm" placeholder="100" />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">De</label>
              <select value={fromCurrency} onChange={e => setFromCurrency(e.target.value)} className="input-glass w-full text-sm">
                {['USD','MXN','EUR','GBP','CAD','JPY','BTC','ETH'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">A</label>
              <select value={toCurrency} onChange={e => setToCurrency(e.target.value)} className="input-glass w-full text-sm">
                {['MXN','USD','EUR','GBP','CAD','JPY','BTC','ETH'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={convertCurrency} className="btn-primary w-full text-sm">Convertir</button>
            </div>
          </div>
          {convertResult && (
            <div className="p-6 bg-blue-500/10 rounded-2xl border border-blue-500/20 text-center">
              <p className="text-gray-400 mb-2">{convertAmount} {fromCurrency} =</p>
              <p className="text-4xl font-bold text-blue-400">{convertResult.converted?.toLocaleString()} {toCurrency}</p>
              <p className="text-gray-500 text-sm mt-2">Tasa: 1 {fromCurrency} = {convertResult.rate} {toCurrency}</p>
            </div>
          )}
        </div>
      )}

      <AIAgentModule sectionContext="finanzas personales, gastos y presupuestos" compact={true} placeholder="Analiza mis gastos o dame consejos financieros..." />
    </div>
  );
};

export default FinanceModule;
